/**
 * Analysis result layers — spec §F6 ("optional result layers on map, registered in legend
 * block (e)") and §K M4's acceptance line, "results appear as legend-managed layers".
 *
 * A result layer is the **third** category of map content, beside the explorer's fill
 * (§F5.3) and the thematic layers (§F5.2). It is not either of them: it is the answer to a
 * question the reader asked and waited for, it has no catalogue row, and it outlives every
 * mode switch that clears the other two (`state.ts::switchSidebarTab`). That is why it has
 * a pane of its own at the top of the stack (`panes.ts::resultPane`, z460): a buffer ring
 * hidden under the settlement polygons it was computed from is a result the reader would
 * report as "the tool did nothing".
 *
 * ## The five kinds (`serializers.RESULT_LAYER_KINDS`)
 *
 * * **`units`** — §H1 compare's highlight. The named `org_units`, outlined 3.4 in the
 *   CMP colour of that unit's row in the chart, filled .12. One palette, read twice: the
 *   line in the chart and the outline on the map are the same colour because the server
 *   built `colors` from the series rows themselves.
 * * **`choropleth`** — §H3 zonal and §H4 suitability. `{unit_id: value}` classified by
 *   `lib/symbology.ts` with the spec's own ramp/method/k, which are §G's defaults
 *   (`green`, quantile, 5) unless the tool says otherwise. **A unit absent from `values`
 *   is not painted at all** rather than painted as a zero: §H3 omits a zone with no valid
 *   pixel under it (NOTES #454), and inventing a class for it would shift every break.
 * * **`geojson` / `points`** — §H7 buffer and §H8 overlay. A FeatureCollection the job
 *   wrote as an artefact, fetched once from §D6's `/analysis/results/{job}.geojson` and
 *   cached by job id, because the artefact is immutable for the life of the job.
 * * **`image`** — §H5 change. A PNG the job wrote, placed by the geographic bounds the
 *   server sends with it. The odd one out in two ways, both deliberate: it is **not
 *   fetched by this client** (the browser loads the `<img>` from the §D6 URL, so there is
 *   no query, no cache entry and no 404 path of our own — an expired artefact is a broken
 *   image the browser reports, not a toast), and it carries **its own legend**, because a
 *   raster overlay has no per-unit values a client could re-classify.
 *
 * ## Geometry comes from the map's own units query
 *
 * `units` and `choropleth` paint `org_units`, and the polygons are already on the client:
 * the same `['units', …]` key the §F5.3 choropleth holds. So a result costs no fetch, and
 * — the part that matters — it is drawn from *exactly* the geometry the explorer is drawn
 * from, at the same generalisation, so a result outline can never sit a pixel off the
 * boundary it describes.
 *
 * The cost of that choice is that a result is only paintable while its units are in the
 * current geography. **That is a dimming, never a deletion** (`useOutOfViewResults`, read
 * by §F5.6 block (e)): deleting a reader's analysis because they changed the country would
 * lose work they waited for, and a note saying it is out of view is both true and
 * recoverable. Changing the country back brings the paint straight back.
 *
 * ## The artefact can expire
 *
 * §E7 reaps artefacts at seven days (`apps.analysis.reaper`), so a permalink or a long
 * session can hold a result layer whose file is gone. The fetch 404s, the layer is removed
 * and the reader is toasted — the one case where this component takes a result off the map
 * by itself, and it does so because the alternative is a legend row for a layer that can
 * never draw.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import {
  API_BASE,
  ApiError,
  getJson,
  queryKeys,
  type FeatureCollection,
  type UnitProperties,
  type UnitsFC,
} from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { escapeHtml } from '../../lib/html';
import {
  useAppStore,
  type ResultLayer as ResultLayerEntry,
  type ResultLayerSpec,
} from '../../lib/state';
import { classify, classOf, FALLBACK_RAMP_STOPS } from '../../lib/symbology';
import { toast } from '../../lib/toast';
import { labelOwner, labelsAtZoom } from './hierarchy';
import { useMap, useMapViewState } from './mapContext';
import { fitTo } from './panes';
import {
  HATCH_PATTERN_ID,
  unitsPathStyle,
  useResultUnits,
} from './resultUnits';
import { createUnitLabels } from './unitLabels';

type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

/** §H3/§H4's choropleth: opaque enough to read a ramp, translucent enough to see the map. */
const CHORO_FILL_OPACITY = 0.74;
const CHORO_STROKE = '#ffffff';
const CHORO_WEIGHT = 1.1;

/** §G defaults, applied when a tool's spec does not name its own classification. */
const DEFAULT_RAMP_KEY = 'green';
const DEFAULT_METHOD = 'quantile';
const DEFAULT_K = 5;

/**
 * §H5's change overlay. 0.85 rather than 1 so the terrain under a changed patch still reads
 * — the PNG's own changed pixels are already written at alpha 230, and the two together
 * leave the basemap visible enough to anchor the patch to the ground it describes.
 */
const IMAGE_OPACITY = 0.85;

/** §H7's "rings result layer (layer colour, fill .13)". */
const ARTIFACT_FILL_OPACITY = 0.13;
const ARTIFACT_WEIGHT = 2.2;
const ARTIFACT_FALLBACK_COLOR = '#2B6CB0';
const ARTIFACT_POINT_RADIUS = 5;

/* ------------------------------------------------------------------ the component */

export function ResultLayers() {
  const map = useMap();
  const { zoom } = useMapViewState(map);
  const results = useAppStore((s) => s.results);
  const level = useAppStore((s) => s.reportingLevel);
  const units = useResultUnits(results.length > 0);

  /**
   * Which result writes the permanent unit names (client round, 3 October 2026), and
   * whether this zoom is deep enough for any — `hierarchy.ts` owns both thresholds, so a
   * UC1 choropleth and the §F5.8 Watersheds row appear at exactly the same zoom.
   *
   * **One owner**, picked by the predicate the §F5.8 rows stand down for
   * (`hierarchy.ts::labelOwner`, read from the other side by `resultLabelLevel`) — so the
   * two sites can never disagree about who is writing a level's names. It skips a one-unit
   * `intel:{uc}:selected` highlight and takes the layer covering the most units.
   */
  const labelled = labelsAtZoom(level, zoom) ? (labelOwner(results)?.id ?? null) : null;

  return (
    <>
      {results.map((result) => (
        <ResultLayer
          key={result.id}
          result={result}
          units={units}
          labelled={result.id === labelled}
        />
      ))}
      <HatchPattern />
      <FitRequests />
    </>
  );
}

/**
 * The one `<pattern>` a `patterns: {id: 'hatched'}` unit is filled through (UC1 launch
 * design §3, "not assessed").
 *
 * It is rendered as an ordinary React element rather than injected into Leaflet's own
 * overlay SVG for two reasons: `url(#…)` resolves against the whole document, so the
 * pattern does not have to live in the same `<svg>` as the paths that reference it; and
 * Leaflet rebuilds that overlay root whenever the renderer is reset, which would take an
 * injected child with it. One instance per map, because `ResultLayers` is mounted once per
 * map. Sized to nothing and pushed out of the flow rather than hidden with `display: none`,
 * which in some engines stops a paint server resolving at all.
 */
function HatchPattern() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="0"
      height="0"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        <pattern
          id={HATCH_PATTERN_ID}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="6" height="6" fill="#f2f4f2" />
          <line x1="0" y1="0" x2="0" y2="6" stroke="#6f7a74" strokeWidth="2" />
        </pattern>
      </defs>
    </svg>
  );
}

/**
 * The §F6 dock asking for the view — `state.fitRequest`.
 *
 * A panel outside the Leaflet tree has no map to move, and giving it one would put a second
 * owner on the view. So "Show change on map" writes bounds and a counter, and this is the
 * one place that reads them. Two rules make it safe to leave a request standing in the
 * store: the counter means an identical box still moves the map (the second press of the
 * same button), and the seq this component starts from is whatever is already there, so a
 * remount — a country change, a tab switch — never replays a fit the reader has moved on
 * from.
 */
function FitRequests() {
  const map = useMap();
  const request = useAppStore((s) => s.fitRequest);
  const served = useRef(request?.seq ?? 0);

  useEffect(() => {
    if (!request || request.seq === served.current) return;
    served.current = request.seq;
    fitTo(map, L.latLngBounds(request.bounds[0], request.bounds[1]));
  }, [map, request]);

  return null;
}

function ResultLayer({
  result,
  units,
  labelled,
}: {
  result: ResultLayerEntry;
  units: UnitsFC | undefined;
  /** Does this layer write the permanent unit names at this zoom? See `ResultLayers`. */
  labelled: boolean;
}) {
  if (result.spec.kind === 'image') return <ImageLayer result={result} />;
  return result.spec.kind === 'geojson' || result.spec.kind === 'points' ? (
    <ArtifactLayer result={result} />
  ) : (
    <UnitsLayer result={result} units={units} labelled={labelled} />
  );
}

/* ------------------------------------------------------- `units` and `choropleth` */

interface UnitPaint {
  style: L.PathOptions;
  /** The extra line the tooltip carries under the unit name, already escaped. */
  detail: string;
}

/**
 * Both org-unit kinds in one component, because they differ only in how a unit gets its
 * colour — the geometry, the pane, the tooltips and the fit are identical, and two
 * components would be two chances for them to drift.
 */
function UnitsLayer({
  result,
  units,
  labelled,
}: {
  result: ResultLayerEntry;
  units: UnitsFC | undefined;
  labelled: boolean;
}) {
  const { t } = useTranslation();
  const map = useMap();
  const spec = result.spec;
  // Client round 2 Oct 2026: a use case's own `units` layer sits at z460, over the §F5.8
  // outlines, so while one is open a click on a watershed reached nothing at all — the
  // hierarchy rows are `fill:false` under a use case and the result's fill swallowed the
  // pointer. The answer is not to lower the result (it is the answer the reader asked
  // for): it is to make the result *be* the unit it is painted from, the same two writes
  // `HierarchyLayers` makes, so a click selects whether it lands on the fill or the line.
  const setSelectedUnit = useAppStore((s) => s.setSelectedUnit);
  const setLastClickLatLng = useAppStore((s) => s.setLastClickLatLng);

  /** unit id → paint + tooltip detail, for the units this result names. */
  const paint = useMemo(() => paintFor(spec), [spec]);

  const features = useMemo(
    () => (units?.features ?? []).filter((f) => paint.has(f.properties.id)),
    [units, paint],
  );

  // One fit per result, on the frame it first has geometry — §H1's "zoom to the compared
  // units". Keyed by the layer id rather than by mount, so a country change that re-runs
  // this effect does not yank the map back to a result the reader has moved on from.
  const fitted = useRef<string | null>(null);

  useEffect(() => {
    if (!features.length) return;
    const layer = L.geoJSON({ type: 'FeatureCollection', features } as unknown as GeoJsonInput, {
      pane: 'resultPane',
      interactive: true,
      style: (feature) => ({
        // `fill: true` even at `fillOpacity: 0` (an `outlineOnly` layer, over a raster
        // chip): Leaflet hit-tests the painted interior, and `fill: false` would render no
        // interior to hit — the outline alone is a 3.4 px target over a whole watershed.
        fill: true,
        ...(paint.get((feature as { properties: UnitProperties }).properties.id)?.style ?? {}),
      }),
      onEachFeature: (feature, lyr) => {
        const props = feature.properties as UnitProperties;
        const detail = paint.get(props.id)?.detail ?? '';
        // Amendment 1 §useUnitLink: the click's own point alongside the unit, so a pane
        // that has to resolve the click (a commune to its watershed) has both.
        if (spec.kind === 'units') {
          lyr.on('click', (e: L.LeafletMouseEvent) => {
            setLastClickLatLng([e.latlng.lat, e.latlng.lng]);
            setSelectedUnit(props.id);
          });
        }
        // `props.name` is `/hierarchy/units` text and `spec.label` is a server-built label
        // (an indicator name, a layer name): both reach `innerHTML` (see `lib/html.ts`).
        lyr.bindTooltip(
          `<b>${escapeHtml(props.name)}</b> — ${escapeHtml(spec.label)}` +
            (detail ? `<br><span style="color:#8B958A">${detail}</span>` : ''),
          { sticky: true, className: 'unit-tip' },
        );
      },
    });
    layer.addTo(map);
    if (fitted.current !== result.id) {
      fitted.current = result.id;
      fitTo(map, layer.getBounds());
    }
    return () => {
      map.removeLayer(layer);
    };
  }, [map, features, paint, spec.label, spec.kind, result.id, t, setSelectedUnit, setLastClickLatLng]);

  /**
   * The permanent names over this result's own units — the same labels the §F5.8 rows draw
   * (`unitLabels.ts`), on the same thresholds, so a watershed is named whether the reader
   * is looking at the hierarchy or at a use case's answer.
   *
   * A second effect rather than a tooltip bound into the layer above: `bindTooltip` is
   * already spent on the value card, Leaflet allows one tooltip per layer, and keeping the
   * labels separate means a `zoomend` across the threshold does not rebuild the
   * choropleth.
   */
  useEffect(() => {
    if (!labelled || !features.length) return;
    const group = createUnitLabels(features).addTo(map);
    return () => {
      map.removeLayer(group);
    };
  }, [map, features, labelled]);

  return null;
}

/** The whole of the difference between the two org-unit kinds. */
function paintFor(spec: ResultLayerSpec): Map<string, UnitPaint> {
  const out = new Map<string, UnitPaint>();

  if (spec.kind === 'units') {
    for (const id of spec.unit_ids ?? []) {
      const detail = spec.details?.[id];
      out.set(id, {
        style: unitsPathStyle(spec, id),
        // Server- or pane-built text (UC1's "not assessed: no pixels"), and this reaches
        // `innerHTML` through `bindTooltip` like every other tooltip line here.
        detail: detail ? escapeHtml(detail) : '',
      });
    }
    return out;
  }

  const values = spec.values ?? {};
  const entries = Object.entries(values).filter(([, v]) => Number.isFinite(v));
  if (!entries.length) return out;
  // The classification the tool asked for. `ramp` names a §G palette; the stops are the
  // module's own fallback rather than `/meta/config.ramps`, because a result layer must
  // paint whether or not the config query has landed, and `green` is what §H3 and §H4 both
  // send. A ramp key this client does not know falls back to green rather than to nothing.
  const stops = RAMP_STOPS[spec.ramp ?? DEFAULT_RAMP_KEY] ?? FALLBACK_RAMP_STOPS;
  const { breaks, colors } = classify(
    entries.map(([, v]) => v),
    { ramp: spec.ramp ?? null, method: spec.method ?? DEFAULT_METHOD, k: spec.k ?? DEFAULT_K, opacity: 1 },
    stops,
  );
  const unitLabel = spec.unit_label ? ` ${spec.unit_label}` : '';
  for (const [id, value] of entries) {
    out.set(id, {
      style: {
        color: CHORO_STROKE,
        weight: CHORO_WEIGHT,
        fillColor: colors[classOf(value, breaks)],
        fillOpacity: CHORO_FILL_OPACITY,
      },
      detail: escapeHtml(`${fmtNum(value, 2)}${unitLabel}`),
    });
  }
  return out;
}

/**
 * The §G ramps a result layer may name, as stops.
 *
 * A small local table rather than `/meta/config.ramps`: this is two ramps deep (§H3 and §H4
 * both send `green`), the values are §G's own, and a result must paint on the frame it is
 * registered rather than one round trip later. `lib/symbology.ts` holds the same green.
 */
const RAMP_STOPS: Record<string, string[]> = {
  green: FALLBACK_RAMP_STOPS,
  brown: ['#F6F1E7', '#E3D3B0', '#C9A96B', '#A8792F', '#7A4E12'],
  blue: ['#EEF4FA', '#C7DCEF', '#90BEDE', '#4E90C4', '#215D97'],
};

/* ------------------------------------------------------------------ artefact-backed */

/** The §D6 artefact path with the API prefix taken off — `getJson` puts it back. */
function artifactPath(uri: string): string {
  return uri.startsWith(API_BASE) ? uri.slice(API_BASE.length) : uri;
}

/**
 * §H7 buffer / §H8 overlay: the FeatureCollection the job wrote.
 *
 * Fetched **once per job** (`queryKeys.analysisResult`) and never refetched: an artefact is
 * immutable for the life of the job that wrote it, so a stale time of "forever" is a
 * statement of fact rather than a cache tuning.
 */
function ArtifactLayer({ result }: { result: ResultLayerEntry }) {
  const { t } = useTranslation();
  const map = useMap();
  const removeResultLayer = useAppStore((s) => s.removeResultLayer);
  const uri = result.spec.uri;

  const { data, error } = useQuery({
    queryKey: queryKeys.analysisResult(result.jobId),
    queryFn: () => getJson<FeatureCollection>(artifactPath(uri as string)),
    enabled: Boolean(uri),
    staleTime: Infinity,
    gcTime: Infinity,
    // A 404 is the §E7 reaper having been past (see the module note), and there is nothing
    // to retry — the file is not coming back.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  // The artefact has expired: take the layer off the map and say so, once. Anything else
  // leaves a legend row for a layer that can never draw.
  useEffect(() => {
    if (error instanceof ApiError && error.status === 404) {
      removeResultLayer(result.id);
      toast(t('analysis.resultGone'));
    }
  }, [error, removeResultLayer, result.id, t]);

  const fitted = useRef<string | null>(null);

  useEffect(() => {
    if (!data?.features?.length) return;
    const color = result.spec.color ?? ARTIFACT_FALLBACK_COLOR;
    const layer = L.geoJSON(data as unknown as GeoJsonInput, {
      pane: 'resultPane',
      style: {
        color,
        weight: ARTIFACT_WEIGHT,
        fillColor: color,
        fillOpacity: ARTIFACT_FILL_OPACITY,
      },
      pointToLayer: (_feature, latlng) =>
        L.circleMarker(latlng, {
          pane: 'resultPane',
          radius: ARTIFACT_POINT_RADIUS,
          color: '#fff',
          weight: 1.4,
          fillColor: color,
          fillOpacity: 0.92,
        }),
      onEachFeature: (_feature, lyr) => {
        lyr.bindTooltip(`<b>${escapeHtml(result.label)}</b>`, {
          sticky: true,
          className: 'unit-tip',
        });
      },
    });
    layer.addTo(map);
    if (fitted.current !== result.id) {
      fitted.current = result.id;
      fitTo(map, layer.getBounds());
    }
    return () => {
      map.removeLayer(layer);
    };
  }, [map, data, result.id, result.label, result.spec.color]);

  return null;
}

/* ----------------------------------------------------------------------- `image` */

/**
 * §H5 change: the PNG the job painted, placed by its own bounds.
 *
 * No fetch and no query. The browser loads the artefact URL as an image, which means the
 * §D6 prefix travels whole (unlike `ArtifactLayer`, which hands its path to `getJson`) and
 * an expired artefact is a broken image rather than a toast — there is no response this
 * component ever sees. That is a deliberate asymmetry with the GeoJSON kinds: adding a HEAD
 * request purely to produce a nicer failure would put a round trip in front of every
 * overlay to improve the one-in-a-thousand case.
 *
 * A spec with no `bounds` draws nothing at all. Leaflet would place an overlay with
 * undefined corners silently and invisibly, and a result the reader waited for that is on
 * the map but nowhere is worse than one that is honestly absent.
 */
function ImageLayer({ result }: { result: ResultLayerEntry }) {
  const map = useMap();
  const { url, bounds } = result.spec;
  const fitted = useRef<string | null>(null);

  useEffect(() => {
    if (!url || !bounds) return;
    const corners = L.latLngBounds(bounds[0], bounds[1]);
    const layer = L.imageOverlay(url, corners, {
      opacity: IMAGE_OPACITY,
      pane: 'resultPane',
      interactive: false,
      alt: result.label,
    });
    layer.addTo(map);
    if (fitted.current !== result.id) {
      fitted.current = result.id;
      fitTo(map, corners);
    }
    return () => {
      map.removeLayer(layer);
    };
  }, [map, url, bounds, result.id, result.label]);

  return null;
}

export default ResultLayers;
